import { CopyOutlined } from "@ant-design/icons";
import { Button, Select, Switch, Tooltip, Typography } from "antd";
import clsx from "clsx";
import { InfoIcon } from "@/shared/assets/svg/icons";
import { supervisionOrgans } from "../../model/mocks/integration-cards";
import { IntegrationCard, IntegrationStatusTypes } from "../../model/types/integration-card";
import { ActionHistoryTable } from "../action-history-table";
import { StatusBadge } from "../status-badge";
import "./supervision-integration-card.scss";

const { Option } = Select;

export type SupervisionIntegrationCardProps = IntegrationCard & {
  onConnect?: (cardId: string) => void;
  onSave?: (cardId: string) => void;
  onDisconnect?: (cardId: string) => void;
  onEdit?: (cardId: string) => void;
  onCopyId?: (id: string) => void;
  onOrganChange?: (cardId: string, organId: string) => void;
  onSwitchChange?: (cardId: string, value: boolean) => void;
  isLoading?: boolean;
};

export const SupervisionIntegrationCard = ({
  id,
  status,
  selectedOrgan,
  integrationId,
  buildBusEnabled = false,
  attributes,
  actionHistory,
  lastAction,
  isEditMode = false,
  onConnect,
  onSave,
  onDisconnect,
  onEdit,
  onCopyId,
  onOrganChange,
  onSwitchChange,
  isLoading = false,
}: SupervisionIntegrationCardProps) => {
  const handleCopyId = () => {
    if (onCopyId && attributes?.connectionId) {
      onCopyId(attributes.connectionId);
    }
  };

  const handleOrganChange = (value: string) => {
    if (onOrganChange) {
      onOrganChange(id, value);
    }
  };

  const handleBuildBusChange = (checked: boolean) => {
    if (onSwitchChange) {
      onSwitchChange(id, checked);
    }
  };

  const isNotConnected = status === IntegrationStatusTypes.NOT_CONNECTED;
  const isConnected = status === IntegrationStatusTypes.CONNECTED && !isEditMode;
  const isEdit = status === IntegrationStatusTypes.CONNECTED && isEditMode;
  const isDisconnected = status === IntegrationStatusTypes.DISCONNECTED;
  const isFirstTimeConnect = isNotConnected && selectedOrgan;
  const isConnectFlow = isNotConnected || isFirstTimeConnect || isEdit;

  const getCardTitle = () => {
    if (isConnectFlow) {
      return "Подключение органа Надзора";
    }
    if (isDisconnected) {
      return "Орган Надзора";
    }
    return selectedOrgan?.name || "Орган Надзора";
  };

  return (
    <div
      className={clsx("supervision-integration-card", {
        "supervision-integration-card--edit": isEdit,
      })}
    >
      {!isConnectFlow && (
        <div
          className={clsx("supervision-integration-card__header", {
            "supervision-integration-card__header--connected": isConnected,
          })}
        >
          <div
            className={clsx("supervision-integration-card__title-wrapper", {
              "supervision-integration-card__title-wrapper--inline": isDisconnected,
            })}
          >
            <Typography.Title
              level={4}
              className="supervision-integration-card__title"
            >
              {getCardTitle()}
            </Typography.Title>
            {isDisconnected && (
              <div className="supervision-integration-card__badge-inline">
                <StatusBadge status={status} />
              </div>
            )}
            {isConnected && (
              <div className="supervision-integration-card__badge-inline">
                <StatusBadge status={status} />
              </div>
            )}
          </div>
          {isConnected && (
            <Button
              type="primary"
              onClick={() => onEdit?.(id)}
              className="supervision-integration-card__edit-btn"
            >
              Изменить настройки
            </Button>
          )}
          {isDisconnected && (
            <Button
              type="primary"
              onClick={() => onConnect?.(id)}
              className="supervision-integration-card__edit-btn"
            >
              Подключить
            </Button>
          )}
        </div>
      )}

      {isConnectFlow && (
        <div className="supervision-integration-card__connect-section">
          <Typography.Title
            level={4}
            className="supervision-integration-card__title"
          >
            {getCardTitle()}
          </Typography.Title>

          <div className="supervision-integration-card__select-row">
            <Select
              placeholder="Найти орган Надзора"
              allowClear
              showSearch
              optionFilterProp="children"
              className="supervision-integration-card__select"
              disabled={isLoading}
              value={selectedOrgan?.id}
              onChange={handleOrganChange}
            >
              {supervisionOrgans.map((organ) => (
                <Option key={organ.id} value={organ.id}>
                  {organ.name}
                </Option>
              ))}
            </Select>
            {!selectedOrgan && (
              <Tooltip
                title={
                  <div className="supervision-integration-card__tooltip">
                    <span>ID : {attributes?.connectionId || integrationId}</span>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={handleCopyId}
                      className="supervision-integration-card__copy-btn"
                    />
                  </div>
                }
              >
                <InfoIcon />
              </Tooltip>
            )}
          </div>
        </div>
      )}

      {(isFirstTimeConnect || isEdit || isConnected) && (
        <div className="supervision-integration-card__settings-section">
          <Typography.Title
            level={5}
            className="supervision-integration-card__section-title"
          >
            Настройки выбранного канала
          </Typography.Title>

          <div className="supervision-integration-card__switch-group">
            <span className="supervision-integration-card__switch-label">
              Шина BuildBus
            </span>
            <Switch
              checked={buildBusEnabled}
              disabled
              onChange={handleBuildBusChange}
            />
          </div>
        </div>
      )}

      {(isFirstTimeConnect || isEdit || isConnected) && (
        <div
          className={clsx("supervision-integration-card__details-row", {
            "supervision-integration-card__details-row--with-action": isEdit && lastAction,
          })}
        >
          <div className="supervision-integration-card__attributes-section">
            <Typography.Title
              level={5}
              className="supervision-integration-card__section-title"
            >
              Атрибуты
            </Typography.Title>
            <div className="supervision-integration-card__attributes">
              <span className="supervision-integration-card__attribute">
                Идентификатор подключения:{" "}
                {attributes?.connectionId || "32498723-WEF"}
              </span>
              <span className="supervision-integration-card__attribute">
                Версия: {attributes?.version || "13.0"}
              </span>
              <span className="supervision-integration-card__attribute">
                Путь подключения:{" "}
                {attributes?.connectionPath || "https://12345.com"}
              </span>
            </div>
          </div>

          {isEdit && lastAction && (
            <div className="supervision-integration-card__last-action-section">
              <Typography.Title
                level={5}
                className="supervision-integration-card__section-title"
              >
                Последнее действие
              </Typography.Title>
              <div className="supervision-integration-card__last-action-content">
                <span className="supervision-integration-card__last-action-user">
                  {lastAction.user}
                </span>
                <span className="supervision-integration-card__last-action-date">
                  {lastAction.date}
                </span>
                <span className="supervision-integration-card__last-action-time">
                  {lastAction.time.replace(/\s*МСК$/, "")}
                </span>
                <span className="supervision-integration-card__last-action-tz">
                  МСК
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="supervision-integration-card__actions">
        {isNotConnected && !selectedOrgan && (
          <Button
            type="primary"
            onClick={() => onConnect?.(id)}
            disabled={isLoading}
            loading={isLoading}
          >
            Подключить
          </Button>
        )}

        {(isFirstTimeConnect || isEdit) && (
          <div className="supervision-integration-card__action-buttons">
            <Button
              type="primary"
              onClick={() => onSave?.(id)}
              disabled={isLoading || !selectedOrgan}
              loading={isLoading}
            >
              Сохранить настройки
            </Button>
            {isEdit && (
              <Button
                onClick={() => onDisconnect?.(id)}
                className="supervision-integration-card__disconnect-btn"
                disabled={isLoading}
                loading={isLoading}
              >
                Отключить интеграцию
              </Button>
            )}
          </div>
        )}
      </div>

      {(isConnected || isDisconnected) &&
        !isEditMode &&
        actionHistory &&
        actionHistory.length > 0 && (
          <div className="supervision-integration-card__history">
            <Typography.Title
              level={5}
              className="supervision-integration-card__section-title"
            >
              История действий
            </Typography.Title>
            <ActionHistoryTable data={actionHistory} />
          </div>
        )}
    </div>
  );
};
